import * as THREE from 'three';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { trackSize } from '../utils/gpu';

export type Quality = 'low' | 'medium' | 'high';
export type QualitySetting = Quality | 'auto';

// low skips post-processing entirely (one direct render, no HalfFloat target), which is what weak phone GPUs need.
// glow scales bloom strength: High draws more additive particles, so its spells and effects need less bloom to match.
// packed draws the scene into 4-byte HDR (R11G11B10 float) instead of 8-byte half float: half the memory traffic,
// which is what phone GPUs run short of, and no visible change (under 0.1% of pixels moved, none by more than 8/255).
// High keeps half float: with 4x MSAA the packed format measured slower on a desktop GPU.
const QUALITY: Record<Quality, { dpr: number; bloom: boolean; glow: number; shadows: boolean; shadowSize: number; msaa: number; particles: number; packed: boolean }> = {
  // dpr caps how sharp phones (devicePixelRatio 2.5-3) render; below ~1.5 the upscaled image looks blurry.
  low: { dpr: 1.5, bloom: false, glow: 1, shadows: false, shadowSize: 512, msaa: 0, particles: 0.45, packed: false },
  medium: { dpr: 2, bloom: true, glow: 1, shadows: true, shadowSize: 1024, msaa: 2, particles: 0.7, packed: true },
  high: { dpr: 3, bloom: true, glow: 0.8, shadows: true, shadowSize: 2048, msaa: 4, particles: 1, packed: false },
};

// Auto walks this ladder: one step down after 2 s of slow frames (< 45 fps), one step back up after a long fast stretch.
// Low walks only its own resolution steps (LOW_STEP onward), so a phone that still lags gets fewer pixels, same look.
const AUTO_STEPS: ReadonlyArray<readonly [Quality, number]> = [
  ['high', 1], ['medium', 1], ['medium', 0.8], ['low', 1], ['low', 0.8], ['low', 0.67],
];
const LOW_STEP = 3;
const WINDOW = 2;
// Per device, not synced: where Auto last settled, so a weak phone starts there instead of lagging down the ladder again.
const STEP_KEY = 'mana-storm-gfx-step-v1';

function setHdr(target: THREE.WebGLRenderTarget, packed: boolean): void {
  target.texture.type = packed ? THREE.UnsignedInt101111Type : THREE.HalfFloatType;
  target.texture.format = packed ? THREE.RGBFormat : THREE.RGBAFormat;
}

function savedStep(): number {
  try {
    const step = Number(localStorage.getItem(STEP_KEY));
    return Number.isInteger(step) ? Math.min(Math.max(step, 0), AUTO_STEPS.length - 1) : 0;
  } catch {
    return 0;
  }
}

export class GameRenderer {
  readonly renderer: THREE.WebGLRenderer;
  private readonly composer: EffectComposer;
  private readonly bloom: UnrealBloomPass;
  private bloomStrength = 0.38;
  private quality: Quality = 'high';
  private setting: QualitySetting = 'high';
  private renderScale = 1;
  private step = 0;
  private autoStart = 0;
  private windowTime = 0;
  private windowFrames = 0;
  private settle = true;
  private fastTime = 0;
  private upAfter = 8;
  private readonly size: { width: number; height: number; dispose: () => void };
  private lastW = 0;
  private lastH = 0;
  private lastDpr = 0;
  private emaDt = 0;
  private shadowTick = 0;
  private readonly packedOk: boolean;

  constructor(
    canvas: HTMLCanvasElement,
    private readonly scene: THREE.Scene,
    private readonly camera: THREE.PerspectiveCamera,
  ) {
    this.size = trackSize(canvas);
    // No MSAA on the canvas itself: Medium/High render through the composer's own MSAA targets, so it was never seen
    // there, and on Low it cost phone GPUs about as much as the whole scene.
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: false, powerPreference: 'high-performance' });
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.1;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFShadowMap;
    this.renderer.info.autoReset = false; // composer renders several passes; count the whole frame

    const gl = this.renderer.getContext() as WebGL2RenderingContext; // three r163+ is WebGL2-only
    // Rendering into R11G11B10 needs EXT_color_buffer_float, and MSAA on it is optional per GPU.
    const samples = this.renderer.extensions.has('EXT_color_buffer_float')
      ? (gl.getInternalformatParameter(gl.RENDERBUFFER, gl.R11F_G11F_B10F, gl.SAMPLES) as Int32Array | null)
      : null;
    this.packedOk = (samples?.[0] ?? 0) >= QUALITY.medium.msaa;

    const target = new THREE.WebGLRenderTarget(1, 1, { type: THREE.HalfFloatType, samples: 4 });
    this.composer = new EffectComposer(this.renderer, target);
    this.composer.addPass(new RenderPass(scene, camera));
    this.bloom = new UnrealBloomPass(new THREE.Vector2(1, 1), 0.38, 0.32, 1.0);
    // Bloom's own targets have no MSAA, so the packed format is a plain win there on every tier.
    if (this.packedOk) for (const t of [this.bloom.renderTargetBright, ...this.bloom.renderTargetsHorizontal, ...this.bloom.renderTargetsVertical]) setHdr(t, true);
    this.composer.addPass(this.bloom);
    // One full-screen pass less: the output pass adds the bloom while tone mapping, instead of the bloom pass first
    // blending it onto the whole scene buffer. Same sum, same image.
    this.bloom.blendMaterial.visible = false; // three skips drawing an invisible material
    const output = new OutputPass();
    const shader = output.material.fragmentShader;
    const read = 'gl_FragColor = texture2D( tDiffuse, vUv );';
    if (!shader.includes(read)) throw new Error('OutputShader changed: bloom is no longer added');
    output.material.fragmentShader = shader
      .replace('uniform sampler2D tDiffuse;', 'uniform sampler2D tDiffuse;\nuniform sampler2D tBloom;')
      .replace(read, `${read}\ngl_FragColor.rgb += texture2D( tBloom, vUv ).rgb;`);
    output.uniforms.tBloom = { value: this.bloom.renderTargetsHorizontal[0].texture }; // where the bloom composites its mips
    this.composer.addPass(output);
  }

  get shadowSize(): number {
    return QUALITY[this.quality].shadowSize;
  }

  get shadowsOn(): boolean {
    return QUALITY[this.quality].shadows;
  }

  // CSS size of the canvas, tracked without forcing a layout.
  get cssSize(): { width: number; height: number } {
    return this.size;
  }

  get tier(): Quality {
    return this.quality;
  }

  get particleDensity(): number {
    return QUALITY[this.quality].particles;
  }

  get graphics(): { setting: QualitySetting; tier: Quality; renderScale: number; degraded: boolean } {
    return { setting: this.setting, tier: this.quality, renderScale: this.renderScale, degraded: this.adaptive && this.step > this.autoStart };
  }

  setQuality(setting: QualitySetting): void {
    this.setting = setting;
    if (!this.adaptive) {
      this.apply(setting as Quality, 1);
      return;
    }
    // Phones start one step down: their screens are dense and their GPUs throttle when hot.
    this.autoStart = setting === 'low' ? LOW_STEP : matchMedia('(pointer: coarse)').matches ? 1 : 0;
    this.fastTime = 0;
    this.upAfter = 8;
    this.applyStep(Math.max(this.autoStart, savedStep())); // a step too low climbs back after a fast stretch
  }

  // Called with each playing frame's time. Returns true when auto changed the tier, so the scene can follow (shadows, lights).
  adapt(dt: number): boolean {
    this.emaDt = this.emaDt === 0 ? dt : this.emaDt * 0.9 + dt * 0.1;
    if (!this.adaptive) return false;
    this.windowTime += dt;
    this.windowFrames += 1;
    if (this.windowTime < WINDOW) return false;
    const avg = this.windowTime / this.windowFrames;
    const settle = this.settle;
    this.windowTime = 0;
    this.windowFrames = 0;
    this.settle = false;
    if (settle) return false; // the first window after a change holds shader compiles and buffer reallocation
    const tier = this.quality;
    if (avg > 1 / 45 && this.step < AUTO_STEPS.length - 1) {
      this.applyStep(this.step + 1);
      this.fastTime = 0;
      this.upAfter = Math.min(120, this.upAfter * 2); // each slowdown makes the next step up more patient: no flip-flopping
    } else if (avg < 1 / 55 && this.step > this.autoStart && this.setting !== 'low') {
      // Low only ever steps down: a phone hovering near the thresholds would otherwise flip resolution back and
      // forth, and each change reallocates the canvas (a visible hitch).
      this.fastTime += WINDOW;
      if (this.fastTime >= this.upAfter) {
        this.applyStep(this.step - 1);
        this.fastTime = 0;
      }
    } else this.fastTime = 0;
    if (this.settle) {
      try {
        localStorage.setItem(STEP_KEY, String(this.step));
      } catch {
        // storage blocked: Auto just relearns next launch
      }
    }
    return this.quality !== tier;
  }

  private get adaptive(): boolean {
    return this.setting === 'auto' || this.setting === 'low';
  }

  private applyStep(step: number): void {
    this.step = step;
    this.apply(...AUTO_STEPS[step]);
  }

  private apply(quality: Quality, renderScale: number): void {
    const q = QUALITY[quality];
    const packed = q.packed && this.packedOk;
    if (q.msaa !== this.composer.renderTarget1.samples || packed !== (this.composer.renderTarget1.texture.type === THREE.UnsignedInt101111Type)) {
      // Takes effect when the targets are next allocated.
      for (const target of [this.composer.renderTarget1, this.composer.renderTarget2]) {
        target.samples = q.msaa;
        setHdr(target, packed);
        target.dispose();
      }
    }
    this.quality = quality;
    this.renderScale = renderScale;
    this.bloom.enabled = q.bloom;
    this.bloom.strength = this.bloomStrength * q.glow;
    this.renderer.shadowMap.enabled = q.shadows;
    this.settle = true;
    this.windowTime = 0;
    this.windowFrames = 0;
    this.lastDpr = 0; // force resize
  }

  // Builds every material's shader now, in the background where the browser can, instead of on the frame an enemy
  // type or effect first shows up: on old phones that first frame freezes. Call after the scene or tier changes.
  warm(): void {
    // Bloom tiers draw the scene into the composer's buffer (tone mapping comes later), so compile for that target.
    if (QUALITY[this.quality].bloom) this.renderer.setRenderTarget(this.composer.readBuffer);
    // Not compileAsync: its readiness polling throws once a stage reload disposes a material it is waiting on. compile()
    // only starts the builds; the browser finishes them off the main thread and three checks them at first draw.
    this.renderer.compile(this.scene, this.camera);
    this.renderer.setRenderTarget(null);
  }

  setBloomStrength(strength: number): void {
    this.bloomStrength = strength;
    this.bloom.strength = strength * QUALITY[this.quality].glow;
  }

  // Returns the drawing-buffer height so callers can size point sprites.
  resize(): number {
    const width = Math.max(1, Math.floor(this.size.width));
    const height = Math.max(1, Math.floor(this.size.height));
    const dpr = Math.min(window.devicePixelRatio || 1, QUALITY[this.quality].dpr) * this.renderScale;
    if (width !== this.lastW || height !== this.lastH || dpr !== this.lastDpr) {
      this.lastW = width;
      this.lastH = height;
      this.lastDpr = dpr;
      this.renderer.setPixelRatio(dpr);
      this.renderer.setSize(width, height, false);
      // Low never uses the composer: sizing it would allocate full-screen half-float MSAA and bloom targets on phones.
      // apply() forces a resize on every tier change, so a tier with bloom gets them sized when it starts.
      if (QUALITY[this.quality].bloom) {
        this.composer.setPixelRatio(dpr);
        this.composer.setSize(width, height);
        this.bloom.setSize(Math.max(1, Math.floor(width * dpr * 0.5)), Math.max(1, Math.floor(height * dpr * 0.5)));
      }
      this.camera.aspect = width / height;
      this.camera.updateProjectionMatrix();
    }
    return height * dpr;
  }

  render(): void {
    this.renderer.info.reset();
    if (QUALITY[this.quality].shadows) {
      if (this.emaDt > 1 / 48) {
        this.renderer.shadowMap.autoUpdate = false;
        this.renderer.shadowMap.needsUpdate = (this.shadowTick++ & 1) === 0;
      } else {
        this.renderer.shadowMap.autoUpdate = true;
      }
    }
    if (QUALITY[this.quality].bloom) this.composer.render();
    else this.renderer.render(this.scene, this.camera); // tone mapping and sRGB output happen in the material shaders
  }

  dispose(): void {
    this.size.dispose();
    this.composer.dispose();
    this.renderer.dispose();
  }
}
